import * as fc from 'utils/fieldConstants';

import { useCreateComparatorOptions } from './useCreateComparatorOptions';
import { useRegexComparatorOptions } from './useRegexComparatorOptions';
import { useIsOneOfComparatorOptions } from './useIsOneOfComparatorOptions';
import { ComparatorSelectType } from '../types';
import {
  AnyComparator,
  BooleanComparator,
  BrandAffinityComparator,
  CampaignComparator,
  CampaignLabelsComparator,
  DateComparator,
  DefaultComparator,
  FieldType,
  GeoLocationComparator,
  ListComparator,
  MessageTypeComparator,
  NumericComparator,
  ObjectComparator,
  StringComparator,
  TemplateComparator,
} from '../../../../types';
import { SelectOption, useConditionStateContext } from '../../../../contexts';
import { CastType } from '../../../../types';
import { useIsCatalogESIndex } from '../../../../hooks/useIsCatalogESIndex';
import { CatalogGeoLocationComparators, CatalogStringComparators } from '../constants';

export const useComparatorOptions = (): SelectOption<AnyComparator>[] => {
  const { field, fieldType, castType } = useConditionStateContext();
  const createComparatorOptions = useCreateComparatorOptions();
  const isCatalogESIndex = useIsCatalogESIndex();
  const regexComparatorOptions = useRegexComparatorOptions();
  const isOneOfComparatorOptions = useIsOneOfComparatorOptions();

  switch (castType) {
    case CastType.EPOCHS_SECS:
    case CastType.EPOCHS_MILLIS:
      return createComparatorOptions(DateComparator, ComparatorSelectType.date);
    default:
      switch (field) {
        case fc.CAMPAIGN:
        case fc.RECURRING_CAMPAIGN:
        case fc.EMAIL_CAMPAIGN:
          return createComparatorOptions(CampaignComparator, ComparatorSelectType.campaign);
        case fc.CANONICAL_URL:
        case fc.LIST_MEMBERSHIP_SINGLE:
        case fc.USER_LIST_MEMBERSHIP_SINGLE:
        case fc.LIST_MEMBERSHIP:
        case fc.USER_LIST_MEMBERSHIP:
          return createComparatorOptions(ListComparator, ComparatorSelectType.list);
        case fc.BRAND_AFFINITY:
          return createComparatorOptions(
            BrandAffinityComparator,
            ComparatorSelectType.brandAffinity,
          );
        case fc.CAMPAIGN_LABEL:
          return createComparatorOptions(
            CampaignLabelsComparator,
            ComparatorSelectType.campaignLabel,
          );
        case fc.MESSAGE_TYPE:
        case fc.SUBSCRIBED_MESSAGE_TYPES:
        case fc.UNSUBSCRIBED_MESSAGE_TYPES:
          return createComparatorOptions(MessageTypeComparator, ComparatorSelectType.messageType);
        case fc.TEMPLATE_ID:
          return createComparatorOptions(TemplateComparator, ComparatorSelectType.template);
        default:
          switch (fieldType) {
            case FieldType.STRING:
              return [
                ...createComparatorOptions(
                  isCatalogESIndex ? CatalogStringComparators : StringComparator,
                  ComparatorSelectType.string,
                ),
                ...isOneOfComparatorOptions,
                ...regexComparatorOptions,
              ];
            case FieldType.FLOAT:
            case FieldType.INTEGER:
              return [
                ...createComparatorOptions(NumericComparator, ComparatorSelectType.number),
                ...isOneOfComparatorOptions,
              ];
            case FieldType.BOOLEAN:
              return createComparatorOptions(BooleanComparator, ComparatorSelectType.boolean);
            case FieldType.DATE:
              return createComparatorOptions(DateComparator, ComparatorSelectType.date);
            case FieldType.OBJECT:
            case FieldType.NESTED:
              return createComparatorOptions(ObjectComparator, ComparatorSelectType.object);
            case FieldType.GEO_LOCATION:
              return createComparatorOptions(
                isCatalogESIndex ? CatalogGeoLocationComparators : GeoLocationComparator,
                ComparatorSelectType.geoLocation,
              );
            default:
              return createComparatorOptions(DefaultComparator, ComparatorSelectType.default);
          }
      }
  }
};
